import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Zap, ShoppingCart, Search, User, Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import usePageTitle from "@/hooks/use-page-title";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import OfferPopup from "@/components/OfferPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com/brothersadvertising7" },
  { icon: Facebook, label: "Facebook", href: "https://facebook.com/brothersadvertising7" },
  { icon: WhatsAppIcon, label: "WhatsApp", href: "https://wa.me/918070970980", brand: "#25D366" },
];

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  usePageTitle();
  const [groups, setGroups] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerIdx, setOfferIdx] = useState(0);
  const [searchQ, setSearchQ] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState("");
  const [hoveredCategory, setHoveredCategory] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState(false);
  const { cart } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    // Single fetch: catalog + header-visible groups + offer strip
    fetch(`${API}/catalog?active_only=true`).then((r) => r.json()).then((rows) => setCatalog(Array.isArray(rows) ? rows : [])).catch(() => {});
    fetch(`${API}/settings/header-groups`).then((r) => r.json()).then((d) => setGroups(Array.isArray(d?.groups) ? d.groups : [])).catch(() => {});
    fetch(`${API}/offers`).then((r) => r.json()).then((rows) => setOffers(Array.isArray(rows) ? rows : [])).catch(() => setOffers([]));
  }, []);

  useEffect(() => {
    if (offers.length <= 1) return;
    const t = setInterval(() => setOfferIdx((i) => (i + 1) % offers.length), 5000);
    return () => clearInterval(t);
  }, [offers.length]);

  const searchResults = searchQ.trim().length >= 2
    ? catalog.filter((p) => `${p.name} ${p.category} ${p.group}`.toLowerCase().includes(searchQ.toLowerCase())).slice(0, 8)
    : [];

  // On-focus "popular" list — trending → best_seller → first 6 by display_order
  const popularProducts = (() => {
    const trending = catalog.filter((p) => p.trending);
    const best = catalog.filter((p) => p.best_seller && !p.trending);
    const rest = catalog.filter((p) => !p.trending && !p.best_seller);
    return [...trending, ...best, ...rest].slice(0, 6);
  })();

  // For group hover mega-menu: derive unique categories + products (matches primary or additional groups)
  const productsInHoveredGroup = hoveredGroup
    ? catalog.filter((p) => p.group === hoveredGroup || (p.groups || []).includes(hoveredGroup))
    : [];
  const categoriesInHoveredGroup = Array.from(
    new Set(
      productsInHoveredGroup
        .flatMap((p) => [p.category, ...(p.categories || [])])
        .filter(Boolean)
    )
  );

  // Category sub-nav hover: narrow the mega-menu product grid to the hovered
  // category (matches primary OR additional categories on the product).
  const megaMenuProducts = hoveredCategory
    ? productsInHoveredGroup.filter((p) => p.category === hoveredCategory || (p.categories || []).includes(hoveredCategory))
    : productsInHoveredGroup;

  // "HOT" = group contains a trending product; "NEW" = group has a product
  // added in the last 30 days. Shown as mini badges on header pills.
  const groupBadges = (g) => {
    const prods = catalog.filter((p) => p.group === g || (p.groups || []).includes(g));
    const badges = [];
    if (prods.some((p) => p.trending)) badges.push("hot");
    const THIRTY_DAYS = 30 * 86400 * 1000;
    if (prods.some((p) => p.created_at && (Date.now() - new Date(p.created_at).getTime()) < THIRTY_DAYS)) badges.push("new");
    return badges;
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      nav(`/products?q=${encodeURIComponent(searchQ.trim())}`);
      setSearchQ("");
      setSearchFocused(false);
      setMobileSearchOpen(false);
      setMobileNavOpen(false);
    }
  };

  // Close mobile drawer whenever the user navigates
  useEffect(() => {
    if (!mobileNavOpen) return;
    // Lock body scroll while drawer is open
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  const currentOffer = offers[offerIdx];
  return (
    <div className="min-h-screen flex flex-col bg-white text-zinc-900">
      {/* Offers strip above header */}
      {currentOffer && (
        <div className="text-white text-xs md:text-sm font-medium py-2 px-4 overflow-hidden" style={{ background: currentOffer.background || "#FF6B00", color: currentOffer.text_color || "#FFFFFF" }} data-testid="offers-strip">
          {currentOffer.marquee ? (
            <div className="whitespace-nowrap inline-block animate-marquee" data-testid="offers-strip-marquee">
              <span>{currentOffer.message}</span>
              {currentOffer.cta_text && currentOffer.cta_url && (
                <Link to={currentOffer.cta_url} className="ml-3 underline font-bold hover:no-underline" data-testid="offers-cta">
                  {currentOffer.cta_text} →
                </Link>
              )}
              <span className="mx-12 opacity-60">•</span>
              <span>{currentOffer.message}</span>
              {currentOffer.cta_text && currentOffer.cta_url && (
                <Link to={currentOffer.cta_url} className="ml-3 underline font-bold hover:no-underline">
                  {currentOffer.cta_text} →
                </Link>
              )}
              <span className="mx-12 opacity-60">•</span>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
              <span>{currentOffer.message}</span>
              {currentOffer.cta_text && currentOffer.cta_url && (
                <Link to={currentOffer.cta_url} className="underline font-bold hover:no-underline" data-testid="offers-cta">
                  {currentOffer.cta_text} →
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header — glass */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        {/* Row 1: logo · full-length search · actions */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 flex items-center gap-2 sm:gap-4 lg:gap-6">
          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-1 hover:text-[#FF6B00] transition-colors"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <Menu size={22} />
          </button>

          <Link to="/" className="inline-flex shrink-0" data-testid="public-logo-link">
            <Logo height={36} variant="light" />
          </Link>

          {/* Desktop full-length search (hidden on mobile) */}
          <form onSubmit={submitSearch} className="relative flex-1 max-w-3xl hidden md:block" data-testid="header-search-form">
            <div className="relative flex items-center">
              <Search size={16} className="absolute left-4 text-zinc-400 pointer-events-none" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                placeholder="Search business cards, flyers, banners…"
                className="w-full h-11 pl-11 pr-24 rounded-full border-2 border-zinc-200 focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white placeholder:text-zinc-400"
                data-testid="header-search-input"
              />
              <button type="submit" className="absolute right-1.5 h-8 px-4 rounded-full bg-[#FF6B00] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#e65a00]" data-testid="header-search-submit">
                Search
              </button>
            </div>
            {/* Live query results */}
            {searchQ.trim().length >= 2 && searchResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border-2 border-zinc-200 rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="header-search-results">
                {searchResults.map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    onClick={() => setSearchQ("")}
                    className="flex justify-between items-center p-3 hover:bg-orange-50 transition-colors border-b border-zinc-100 last:border-0"
                    data-testid={`header-search-result-${p.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{p.group} · {p.category}</div>
                    </div>
                    <div className="font-mono text-[#FF6B00] font-bold text-xs shrink-0 ml-3">
                      ₹{Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1)).toLocaleString("en-IN")}
                    </div>
                  </Link>
                ))}
              </div>
            )}
            {/* On-focus popular suggestions (empty query) */}
            {searchFocused && searchQ.trim().length < 2 && popularProducts.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border-2 border-zinc-200 rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="header-search-popular">
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                  <span>🔥</span> Popular right now
                </div>
                {popularProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSearchQ(""); setSearchFocused(false); }}
                    className="flex justify-between items-center p-3 hover:bg-orange-50 transition-colors border-b border-zinc-100 last:border-0"
                    data-testid={`header-search-popular-${p.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {p.image_url && (
                        <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-sm truncate">{p.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{p.group}{p.trending ? " · Trending" : (p.best_seller ? " · Best Seller" : "")}</div>
                      </div>
                    </div>
                    <div className="font-mono text-[#FF6B00] font-bold text-xs shrink-0 ml-3">
                      ₹{Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1)).toLocaleString("en-IN")}
                    </div>
                  </Link>
                ))}
                <Link to="/products" onMouseDown={(e) => e.preventDefault()} onClick={() => setSearchFocused(false)} className="block bg-orange-50 hover:bg-orange-100 text-center p-3 text-sm font-bold text-[#FF6B00]" data-testid="header-search-view-all">
                  View all {catalog.length} products →
                </Link>
              </div>
            )}
          </form>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto">
            {/* Mobile-only search icon */}
            <button
              type="button"
              onClick={() => setMobileSearchOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 hover:text-[#FF6B00] transition-colors"
              aria-label="Search"
              data-testid="mobile-search-btn"
            >
              <Search size={20} />
            </button>
            <Link to="/cart" className="relative inline-flex items-center justify-center w-10 h-10 hover:text-[#FF6B00] transition-colors" data-testid="public-cart-btn" aria-label="Cart">
              <ShoppingCart size={18} />
              {cart?.count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[#FF6B00] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="cart-badge">
                  {cart.count}
                </span>
              )}
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <Link to="/track" className="btn-swiss btn-outline" data-testid="public-track-btn">
                Track Order
              </Link>
              {user && user !== false ? (
                <Link to={user.role === "customer" ? "/portal" : "/dashboard"} className="btn-swiss btn-accent items-center gap-1.5" data-testid="public-account-btn">
                  <User size={12} /> {user.name?.split(" ")[0] || "Account"}
                </Link>
              ) : (
                <Link to="/login" className="btn-swiss btn-accent" data-testid="public-login-btn">
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Row 1.5 — mobile-only expanded search input */}
        {mobileSearchOpen && (
          <div className="md:hidden border-t border-zinc-100 bg-white px-3 py-3" data-testid="mobile-search-panel">
            <form onSubmit={submitSearch} className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search business cards, flyers, banners…"
                className="w-full h-11 pl-11 pr-24 rounded-full border-2 border-zinc-200 focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white placeholder:text-zinc-400"
                data-testid="mobile-search-input"
              />
              <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-4 rounded-full bg-[#FF6B00] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#e65a00]">
                Search
              </button>
            </form>
            {searchQ.trim().length >= 2 && searchResults.length > 0 && (
              <div className="mt-2 bg-white border-2 border-zinc-200 rounded-2xl overflow-hidden" data-testid="mobile-search-results">
                {searchResults.map((p) => (
                  <Link key={p.id} to={`/products/${p.id}`}
                    onClick={() => { setSearchQ(""); setMobileSearchOpen(false); }}
                    className="flex justify-between items-center p-3 hover:bg-orange-50 border-b border-zinc-100 last:border-0"
                    data-testid={`mobile-search-result-${p.id}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{p.group} · {p.category}</div>
                    </div>
                    <div className="font-mono text-[#FF6B00] font-bold text-xs shrink-0 ml-3">
                      ₹{Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1)).toLocaleString("en-IN")}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Row 2: product groups strip (Amazon-style) — desktop/tablet only */}
        {groups.length > 0 && (
          <div className="hidden md:block border-t border-zinc-100 bg-white/50 relative" data-testid="groups-strip" onMouseLeave={() => setHoveredGroup("")}>
            <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center gap-1 overflow-x-auto scrollbar-hide">
              <NavLink
                to="/products"
                end
                onMouseEnter={() => { setHoveredGroup(""); setHoveredCategory(""); }}
                className={({ isActive }) =>
                  `whitespace-nowrap text-xs font-bold uppercase tracking-wider px-3 py-3 border-b-2 transition-colors ${isActive ? "text-[#FF6B00] border-[#FF6B00]" : "text-zinc-700 border-transparent hover:text-[#FF6B00]"}`
                }
                data-testid="public-nav-all-products"
              >
                All Products
              </NavLink>
              <div className="w-px h-4 bg-zinc-200 mx-1" />
              {groups.map((g) => (
                <NavLink
                  key={g}
                  to={`/products?g=${encodeURIComponent(g)}`}
                  onMouseEnter={() => { setHoveredGroup(g); setHoveredCategory(""); }}
                  className={({ isActive }) =>
                    `whitespace-nowrap inline-flex items-center gap-1.5 text-xs font-bold px-3 py-3 border-b-2 transition-colors ${isActive || hoveredGroup === g ? "text-[#FF6B00] border-[#FF6B00]" : "text-zinc-700 border-transparent hover:text-[#FF6B00]"}`
                  }
                  data-testid={`group-strip-${g.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {g}
                  {groupBadges(g).map((b) => (
                    <span
                      key={b}
                      className={`text-[8px] font-black tracking-widest px-1.5 py-0.5 rounded-full leading-none ${b === "hot" ? "bg-red-600 text-white" : "bg-emerald-500 text-white"}`}
                      data-testid={`group-badge-${b}-${g.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {b === "hot" ? "HOT" : "NEW"}
                    </span>
                  ))}
                </NavLink>
              ))}
            </div>

            {/* Hover mega-menu — categories left, featured products right */}
            {hoveredGroup && productsInHoveredGroup.length > 0 && (
              <div className="absolute left-0 right-0 top-full bg-white border-b border-zinc-200 shadow-2xl z-40 animate-in fade-in slide-in-from-top-2 duration-150" data-testid={`group-megamenu-${hoveredGroup.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-12 gap-6">
                  {/* Left: category links */}
                  <div className="col-span-4 lg:col-span-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-black mb-3">{hoveredGroup}</div>
                    <ul className="space-y-2">
                      <li>
                        <Link
                          to={`/products?g=${encodeURIComponent(hoveredGroup)}`}
                          onClick={() => setHoveredGroup("")}
                          className="text-sm font-bold text-zinc-900 hover:text-[#FF6B00] transition-colors block"
                          data-testid={`megamenu-view-all-${hoveredGroup.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          View all {productsInHoveredGroup.length} products →
                        </Link>
                      </li>
                      {categoriesInHoveredGroup.length > 0 && (
                        <li className="pt-2 mt-2 border-t border-zinc-100">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Browse by Category</div>
                          <ul className="space-y-1.5">
                            {categoriesInHoveredGroup.map((c) => (
                              <li key={c}>
                                <Link
                                  to={`/products?g=${encodeURIComponent(hoveredGroup)}&q=${encodeURIComponent(c)}`}
                                  onMouseEnter={() => setHoveredCategory(c)}
                                  onClick={() => { setHoveredGroup(""); setHoveredCategory(""); }}
                                  className={`text-sm transition-colors block ${hoveredCategory === c ? "text-[#FF6B00] font-bold" : "text-zinc-700 hover:text-[#FF6B00]"}`}
                                  data-testid={`megamenu-category-${c.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {c}
                                  <span className="ml-1.5 text-[10px] text-zinc-400 font-normal">
                                    ({productsInHoveredGroup.filter((p) => p.category === c || (p.categories || []).includes(c)).length})
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </div>
                  {/* Right: featured products — narrows to hovered category */}
                  <div className="col-span-8 lg:col-span-9">
                    {hoveredCategory && (
                      <div className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-black mb-3" data-testid="megamenu-category-heading">
                        {hoveredCategory} <span className="text-zinc-400 font-normal">({megaMenuProducts.length} products)</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {megaMenuProducts.slice(0, 8).map((p) => (
                      <Link
                        key={p.id}
                        to={`/products/${p.id}`}
                        onClick={() => setHoveredGroup("")}
                        className="group flex flex-col rounded-lg overflow-hidden border border-zinc-100 hover:border-[#FF6B00] hover:shadow-md transition-all"
                        data-testid={`megamenu-product-${p.id}`}
                      >
                        {p.image_url ? (
                          <div className="aspect-square bg-zinc-50 overflow-hidden">
                            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = "none"; }} />
                          </div>
                        ) : (
                          <div className="aspect-square bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center px-3">
                            <div className="text-xs font-bold text-zinc-500 text-center leading-tight">{p.name}</div>
                          </div>
                        )}
                        <div className="p-2">
                          <div className="text-xs font-bold text-zinc-800 group-hover:text-[#FF6B00] truncate">{p.name}</div>
                          <div className="text-[10px] font-mono text-[#FF6B00] font-bold mt-0.5">
                            ₹{Math.round((p.quantity_tiers?.[0]?.unit_price ?? p.base_price) * (p.min_quantity || 1)).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </Link>
                    ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Mobile navigation drawer — slides in from the left */}
      {mobileNavOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden"
            onClick={() => setMobileNavOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className="fixed top-0 left-0 z-50 h-full w-[85%] max-w-sm bg-white shadow-2xl md:hidden overflow-y-auto animate-in slide-in-from-left duration-200"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <Link to="/" onClick={() => setMobileNavOpen(false)}>
                <Logo height={32} variant="light" />
              </Link>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-zinc-100"
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Primary nav */}
            <nav className="px-2 py-3" data-testid="mobile-drawer-nav">
              {NAV.filter((n) => n.to === "/services").map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors ${isActive ? "text-[#FF6B00] bg-orange-50" : "text-zinc-800 hover:bg-zinc-50"}`
                  }
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <NavLink
                to="/products"
                end
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors ${isActive ? "text-[#FF6B00] bg-orange-50" : "text-zinc-800 hover:bg-zinc-50"}`
                }
                data-testid="mobile-nav-all-products"
              >
                All Products
              </NavLink>

              {/* Product groups — collapsible section */}
              {groups.length > 0 && (
                <div className="mt-2 border-t border-zinc-100 pt-2">
                  <button
                    type="button"
                    onClick={() => setMobileGroupsOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-bold uppercase tracking-wider text-zinc-800 hover:bg-zinc-50"
                    data-testid="mobile-groups-toggle"
                  >
                    <span>Shop by Group</span>
                    <ChevronDown size={16} className={`transition-transform ${mobileGroupsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileGroupsOpen && (
                    <div className="pl-3 space-y-0.5 pb-2">
                      {groups.map((g) => (
                        <Link
                          key={g}
                          to={`/products?g=${encodeURIComponent(g)}`}
                          onClick={() => setMobileNavOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-700 hover:bg-orange-50 hover:text-[#FF6B00]"
                          data-testid={`mobile-group-${g.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {g}
                          {groupBadges(g).map((b) => (
                            <span
                              key={b}
                              className={`text-[8px] font-black tracking-widest px-1.5 py-0.5 rounded-full leading-none ${b === "hot" ? "bg-red-600 text-white" : "bg-emerald-500 text-white"}`}
                              data-testid={`mobile-group-badge-${b}-${g.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {b === "hot" ? "HOT" : "NEW"}
                            </span>
                          ))}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Utility actions */}
            <div className="px-4 py-3 border-t border-zinc-100 space-y-2">
              <Link
                to="/track"
                onClick={() => setMobileNavOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 border-2 border-zinc-200 text-zinc-800 font-bold px-4 py-2.5 rounded-full text-sm hover:border-[#FF6B00] hover:text-[#FF6B00]"
                data-testid="mobile-track-btn"
              >
                Track Order
              </Link>
              {user && user !== false ? (
                <Link
                  to={user.role === "customer" ? "/portal" : "/dashboard"}
                  onClick={() => setMobileNavOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold px-4 py-2.5 rounded-full text-sm hover:bg-[#e65a00]"
                  data-testid="mobile-account-btn"
                >
                  <User size={14} /> {user.name?.split(" ")[0] || "My Account"}
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileNavOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white font-bold px-4 py-2.5 rounded-full text-sm hover:bg-[#e65a00]"
                  data-testid="mobile-login-btn"
                >
                  Login / Sign up
                </Link>
              )}
            </div>

            {/* Contact quick-actions */}
            <div className="px-4 py-4 border-t border-zinc-100 space-y-2 text-sm">
              <a href="tel:+918070970980" className="flex items-center gap-2 text-zinc-700 hover:text-[#FF6B00]">
                <Phone size={14} /> +91 80709 70980
              </a>
              <a href="mailto:print@brothersdigital.in" className="flex items-center gap-2 text-zinc-700 hover:text-[#FF6B00]">
                <Mail size={14} /> print@brothersdigital.in
              </a>
              <div className="flex items-center gap-3 pt-1">
                {SOCIALS.map(({ icon: Icon, label, href, brand }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                     className="w-9 h-9 inline-flex items-center justify-center rounded-full border-2 border-zinc-200 hover:border-[#FF6B00] hover:text-[#FF6B00]"
                     style={brand ? { color: brand } : {}}>
                    <Icon size={14} />
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </>
      )}

      <main className="flex-1">
        <Outlet />
        <OfferPopup />
      </main>

      <footer className="bg-zinc-900 text-zinc-300 pt-14 pb-6">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="inline-flex mb-4"><Logo height={38} variant="dark" /></div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Premium printing, signage & branding solutions. Serving individuals, startups, corporates and institutions across Navi Mumbai and beyond.
            </p>
            <div className="flex gap-3 mt-4">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                  className="p-2 border border-zinc-700 hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
                  style={s.brand ? { borderColor: "transparent" } : undefined}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  aria-label={s.label}
                  {...(s.brand ? { onMouseOver: () => {} } : {})}>
                  <s.icon size={14} style={s.brand ? { color: s.brand } : undefined} />
                </a>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white font-bold mb-4">Quick Links</div>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} className="hover:text-[#FF6B00]">{n.label}</Link></li>
              ))}
              <li><Link to="/track" className="hover:text-[#FF6B00]">Track Order</Link></li>
              <li><Link to="/login" className="hover:text-[#FF6B00]">Customer Login</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white font-bold mb-4">Get in Touch</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2"><Phone size={14} className="text-[#FF6B00] mt-0.5" /><div><div className="text-[10px] uppercase tracking-wider text-zinc-400">Customer Care</div><a href="tel:02247427059" className="hover:text-[#FF6B00]">022 4742 7059</a></div></li>
              <li className="flex items-start gap-2"><Phone size={14} className="text-[#FF6B00] mt-0.5" /><div><div className="text-[10px] uppercase tracking-wider text-zinc-400">Mobile / WhatsApp</div><a href="tel:918070970980" className="hover:text-[#FF6B00]">+91 80709 70980</a></div></li>
              <li className="flex items-start gap-2"><Mail size={14} className="text-[#FF6B00] mt-0.5" /><a href="mailto:print@brothersdigital.in">print@brothersdigital.in</a></li>
              <li className="flex items-start gap-2"><MapPin size={14} className="text-[#FF6B00] mt-0.5" /><span>Shop No. 02, Payal Residency, Plot No. 29, Sector 16, Taloja Phase 2, Navi Mumbai — 410208</span></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white font-bold mb-4">Business Hours</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li>Mon – Sun · 09:00 AM – 09:00 PM</li>
              <li>Open all 7 days</li>
            </ul>
            <div className="mt-6 text-xs text-zinc-500">A unit of J U ENTERPRISES</div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-10 pt-6 border-t border-zinc-800 flex flex-wrap justify-between gap-4 text-xs text-zinc-500">
          <div>© 2026 Brothers Advertising™. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#FF6B00]" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#FF6B00]" data-testid="footer-terms">Terms</Link>
            <Link to="/shipping-delivery" className="hover:text-[#FF6B00]" data-testid="footer-shipping">Shipping & Delivery</Link>
            <Link to="/returns-refund" className="hover:text-[#FF6B00]" data-testid="footer-returns">Returns & Refund</Link>
            <Link to="/careers" className="hover:text-[#FF6B00]" data-testid="footer-careers">Careers</Link>
          </div>
        </div>
      </footer>

      {/* Floating CTAs */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2" data-testid="floating-ctas">
        <a
          href="https://wa.me/918070970980?text=Hi%20Brothers%20Advertising%2C%20I%27d%20like%20a%20quick%20quote%20for%20printing"
          target="_blank"
          rel="noreferrer"
          className="w-12 h-12 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid="fab-whatsapp"
          aria-label="WhatsApp us"
        >
          <WhatsAppIcon size={24} />
        </a>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF6B00] text-white font-bold text-sm px-5 py-3 shadow-lg hover:bg-[#e65a00] hover:scale-105 transition-transform"
          data-testid="fab-quick-quote"
        >
          <Zap size={16} /> Quick Quote
        </Link>
      </div>
    </div>
  );
}
